import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, RefreshCw, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { conteggio, euro, numero, percentuale } from "@/lib/format";
import { giornoOra, TIPO_APPUNTAMENTO } from "@/lib/crm";
import { messaggioDi } from "@/lib/messaggio-errore";
import { useCountUp } from "@/hooks/use-count-up";
import { SoldChartBody } from "@/components/charts/sold-chart";
import { Segmented } from "@/components/ui/segmented";
import { StatusBadge } from "@/components/ui/status-badge";
import { ListRow } from "@/components/ui/list-row";
import { ErrorState } from "@/components/ui/states";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * La Dashboard del titolare (tavola U-dashboard del 29-10). Risponde in cinque secondi a tre domande, per
 * sede e periodo: quanto abbiamo venduto (firmato), chi sta arrivando, cosa è fermo.
 *
 * I numeri arrivano da GET /api/cruscotto (funzione `cruscotto_numeri`, 0027), che risponde solo al
 * titolare. «Venduto» è il valore dei contratti FIRMATI, mai il fatturato: Interni D'Autore fattura mesi
 * dopo la consegna. Niente ROAS né moltiplicatori. Le parti della tavola che l'API non dà (visite fissate,
 * presentati e no show del mese, «Da fare oggi» con i suoi pulsanti, la ricerca) non ci sono: non si
 * inventano numeri.
 */

const PERIODI = [
  { valore: "mese", etichetta: "Questo mese" },
  { valore: "mese_scorso", etichetta: "Mese scorso" },
  { valore: "tre_mesi", etichetta: "Ultimi tre mesi" },
  { valore: "anno", etichetta: "Da inizio anno" },
];

const TZ = "Europe/Rome";
const MESI = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const BREVI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const fmtOggi = new Intl.DateTimeFormat("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: TZ });
const fmtOra = new Intl.DateTimeFormat("it-IT", { hour: "numeric", minute: "2-digit", timeZone: TZ });
const n = (x) => Number(x) || 0;
const maiuscola = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function leggiSede() {
  try {
    return localStorage.getItem("ida:sede") || "all";
  } catch (_) {
    return "all";
  }
}

/** «a ottobre», «da agosto a ottobre», «nel 2026»: il periodo come lo direbbe una persona. */
function nomePeriodo(p) {
  if (!p?.da) return "";
  const da = new Date(`${p.da}T12:00:00Z`);
  const a = new Date(`${p.a}T12:00:00Z`);
  if (p.chiave === "anno") return `nel ${da.getUTCFullYear()}`;
  if (p.chiave === "tre_mesi") return `da ${MESI[da.getUTCMonth()]} a ${MESI[a.getUTCMonth()]}`;
  return `a ${MESI[da.getUTCMonth()]}`;
}

/** I 12 mesi del grafico: l'ultimo è quello in corso; l'anno si scrive solo se non è quello di oggi. */
function mesiDelGrafico(mesi, oggi) {
  const anno = Number(String(oggi || "").slice(0, 4));
  return (mesi || []).map((m, i, tutti) => {
    const [a, mm] = String(m.mese).split("-").map(Number);
    const corrente = i === tutti.length - 1;
    const nome = maiuscola(MESI[mm - 1]) + (corrente ? ", in corso" : a !== anno ? ` ${a}` : "");
    return { short: BREVI[mm - 1], name: nome, total: n(m.venduto), contracts: n(m.contratti), current: corrente };
  });
}

/* Il numero che conta all'apertura (650 ms, una volta sola). Chi usa un lettore di schermo sente il valore
   finale, non i passaggi. */
function Contato({ valore, formato = euro, className, testId, anima }) {
  const v = useCountUp(n(valore));
  return (
    <span data-testid={testId} className={className}>
      <span aria-hidden="true">{formato(anima ? v : n(valore))}</span>
      <span className="sr-only">{formato(n(valore))}</span>
    </span>
  );
}

function Scheda({ titolo, azione, children, className, testId, id }) {
  const titoloId = useId();
  return (
    <section
      id={id}
      aria-labelledby={titoloId}
      data-testid={testId}
      className={cn("flex min-w-0 animate-ida-rise flex-col gap-3 rounded-xl border border-line bg-surface p-5 md:px-6", className)}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-2">
        <h2 id={titoloId} className="font-display text-card-title text-ink-strong">
          {titolo}
        </h2>
        {azione}
      </div>
      {children}
    </section>
  );
}

function Riquadro({ etichetta, valore, nota, testId }) {
  return (
    <div className="rounded-md bg-page px-3 py-2.5">
      <dt className="text-small text-ink-3">{etichetta}</dt>
      <dd className="mt-0.5 font-bold tabular-nums text-ink-strong" data-testid={testId}>
        {valore}
      </dd>
      {nota ? <dd className="text-caption font-normal text-ink-2">{nota}</dd> : null}
    </div>
  );
}

/* ── Quanto abbiamo venduto ──────────────────────────────────────────────── */
function Venduto({ d, perSede, anima }) {
  const [vista, setVista] = useState("chart");
  const contratti = n(d.venduto.contratti);
  const storico = n(d.venduto_storico?.importo);
  const a = d.andamento;
  const obiettivo = n(a.obiettivo_anno);
  const daGennaio = n(a.da_inizio_anno);
  const mese = Number(String(d.periodo?.oggi || "").slice(5, 7)) || new Date().getMonth() + 1;
  const alPasso = n(a.passo_mensile) * mese;
  const mancano = Math.max(0, obiettivo - daGennaio);
  const restano = 12 - mese;
  const ritmo = a.mesi_media
    ? a.mesi_media === 1
      ? MESI[(mese + 10) % 12]
      : `${MESI[(mese - 1 - a.mesi_media + 12) % 12]}–${MESI[(mese + 10) % 12]}`
    : null;
  const quota = n(d.netto.imponibile) > 0 ? n(d.netto.importo) / n(d.netto.imponibile) : null;
  /* La divisione per sede vale solo se è dello stesso periodo del totale (le risposte arrivano in ordine
     sparso). La differenza si arrotonda al centesimo: in virgola mobile resterebbe un 7e-12 «senza sede». */
  const sedi = perSede && perSede.periodo === d.periodo?.chiave && !d.sede_id ? perSede.valori : null;
  const senzaSede = sedi ? Math.round((n(d.venduto.importo) - sedi.reduce((s, x) => s + x.importo, 0)) * 100) / 100 : 0;
  const mesi = mesiDelGrafico(a.mesi, d.periodo?.oggi);
  const tuttoAZero = mesi.every((m) => m.total === 0);

  const avvisi = [];
  if (n(d.netto.senza_costo) > 0) {
    // I contratti fatti fuori non hanno mai il costo (c36 D17): senza il «di cui» l'avviso sarebbe permanente.
    const fuori = n(d.netto.senza_costo_esterni);
    avvisi.push(
      `${d.netto.senza_costo} ${d.netto.senza_costo === 1 ? "contratto firmato non ha" : "contratti firmati non hanno"} il costo: ${d.netto.senza_costo === 1 ? "è fuori" : "sono fuori"} dal netto${fuori > 0 ? ` (di cui ${fuori} ${fuori === 1 ? "fatto fuori" : "fatti fuori"})` : ""}.`,
    );
  }
  if (n(d.venduto.senza_totale) > 0)
    avvisi.push(`${d.venduto.senza_totale} ${d.venduto.senza_totale === 1 ? "contratto firmato è" : "contratti firmati sono"} senza importo.`);
  if (n(d.firmati_senza_data) > 0)
    avvisi.push(
      `${d.firmati_senza_data} ${d.firmati_senza_data === 1 ? "contratto firmato non ha" : "contratti firmati non hanno"} la data di firma: non ${d.firmati_senza_data === 1 ? "entra" : "entrano"} in nessun mese.`,
    );

  return (
    <Scheda
      titolo="Quanto abbiamo venduto"
      testId="cruscotto-testata"
      className="flex-[2_1_600px] gap-4 md:px-7 md:pb-[26px] md:pt-6"
      azione={
        <Segmented
          aria-label="Mostra come"
          size="sm"
          value={vista}
          onValueChange={setVista}
          options={[
            { value: "chart", label: "Grafico" },
            { value: "table", label: "Tabella" },
          ]}
        />
      }
    >
      <div className="grid gap-8 [grid-template-columns:repeat(auto-fit,minmax(min(280px,100%),1fr))]">
        <div className="flex min-w-0 flex-col gap-5">
          <div>
            <p className="text-ink-2">Venduto firmato {nomePeriodo(d.periodo)}</p>
            <Contato valore={d.venduto.importo} anima={anima} testId="cruscotto-venduto" className="mt-0.5 block font-figure text-display text-ink-strong md:text-hero" />
            <p className="mt-1 text-ink-2">{contratti === 0 ? "Nessun contratto firmato in questo periodo." : conteggio(contratti, "contratto firmato", "contratti firmati")}</p>
            {storico > 0 ? (
              <p className="text-ink-2" data-testid="cruscotto-venduto-storico">
                di cui {euro(storico)} dai fogli Excel di prima
              </p>
            ) : null}
          </div>

          {sedi ? (
            <dl className="grid grid-cols-2 gap-2" data-testid="cruscotto-per-sede">
              {sedi.map((s) => (
                <Riquadro key={s.id} etichetta={s.nome} valore={euro(s.importo)} />
              ))}
              {senzaSede >= 1 ? <Riquadro etichetta="Senza sede" valore={euro(senzaSede)} /> : null}
            </dl>
          ) : null}

          <dl className="grid grid-cols-2 gap-2">
            <Riquadro
              etichetta="Netto sulle vendite"
              valore={euro(n(d.netto.importo))}
              testId="cruscotto-netto"
              nota={quota === null ? "senza IVA, meno i costi" : `${percentuale(quota, 0)} del venduto senza IVA`}
            />
            <Riquadro
              etichetta="Da incassare"
              valore={euro(n(d.da_incassare.importo))}
              testId="cruscotto-da-incassare"
              nota={n(d.da_incassare.contratti) === 0 ? "niente in sospeso" : `su ${conteggio(n(d.da_incassare.contratti), "contratto firmato", "contratti firmati")}`}
            />
          </dl>

          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              <h3 className="text-body-strong text-ink-strong">Verso il milione</h3>
              <span className="tabular-nums text-ink-2">
                <strong className="text-ink">{euro(daGennaio)}</strong> da gennaio
              </span>
            </div>
            <div className="relative pt-5">
              <span className="absolute right-0 top-0 text-caption font-normal tabular-nums text-ink-2">al passo oggi: {euro(alPasso)}</span>
              <div
                role="img"
                aria-label={`${euro(daGennaio)} su ${euro(obiettivo)}; al passo del milione oggi servirebbero ${euro(alPasso)}.`}
                className="relative h-3 rounded-[6px] bg-track"
              >
                <span
                  className={cn("absolute inset-y-0 left-0 rounded-[6px] bg-brand", anima && "ida-grow-x")}
                  style={{ width: `${Math.min(100, (daGennaio / (obiettivo || 1)) * 100)}%` }}
                />
                <span aria-hidden="true" className="absolute -inset-y-1 w-0.5 rounded-[1px] bg-ink" style={{ left: `${Math.min(100, (alPasso / (obiettivo || 1)) * 100)}%` }} />
              </div>
            </div>
            <p className="text-small tabular-nums text-ink-2">
              {mancano === 0 ? "Il milione è raggiunto. " : restano > 0 ? `Mancano ${euro(mancano)} in ${conteggio(restano, "mese", "mesi")}. ` : `Mancano ${euro(mancano)} entro dicembre. `}
              {ritmo ? <>Al ritmo di {ritmo} l'anno chiude a </> : <>Al ritmo attuale: </>}
              <strong className="text-ink" data-testid="cruscotto-proiezione">
                {a.proiezione_anno === null ? "—" : euro(n(a.proiezione_anno))}
              </strong>
              {ritmo ? "." : " · Serve almeno un mese chiuso con contratti firmati."}
            </p>
          </div>

          {avvisi.length ? (
            <ul className="flex flex-col gap-1 text-small text-ink-2">
              {avvisi.map((t) => (
                <li key={t} className="flex items-start gap-1.5">
                  <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning" strokeWidth={2} aria-hidden="true" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex min-w-0 flex-col gap-2" data-testid="cruscotto-andamento">
          <p className="text-ink-2">Mese per mese, in migliaia di euro</p>
          {tuttoAZero ? (
            <p className="rounded-md bg-page px-4 py-6 text-center text-ink-2">Nessun contratto firmato negli ultimi 12 mesi. Il grafico si riempie col primo.</p>
          ) : (
            <>
              <SoldChartBody months={mesi} view={vista} pace={n(a.passo_mensile)} paceLabel={null} anima={anima} />
              <p className="flex flex-wrap items-center gap-x-2 text-caption font-normal text-ink-2">
                <span aria-hidden="true" className="inline-block w-5 border-t-[1.5px] border-dashed border-ink-3" />
                <span>
                  Passo del milione: {euro(n(a.passo_mensile))} al mese. {maiuscola(MESI[mese - 1])} è in corso.
                </span>
              </p>
            </>
          )}
        </div>
      </div>
    </Scheda>
  );
}

/* ── Chi sta arrivando · Cosa è fermo ──────────────────────────────────────── */
function NumeroGrande({ valore, colore = "text-ink-strong", dopo, anima }) {
  return (
    <span className="flex flex-wrap items-baseline gap-x-2.5">
      <Contato valore={valore} formato={numero} anima={anima} className={cn("font-figure text-display", colore)} />
      <span className="font-semibold">{dopo}</span>
    </span>
  );
}

function ChiArriva({ d, anima }) {
  const elenco = d.fonti.elenco || [];
  const prima = elenco.find((x) => x.codice !== "non_indicata");
  const senza = n(elenco.find((x) => x.codice === "non_indicata")?.clienti);
  const app = n(d.appuntamenti.numero);
  return (
    <Scheda
      titolo="Chi sta arrivando"
      testId="cruscotto-arrivi"
      className="flex-1 gap-2.5"
      azione={<span className="text-small text-ink-3">{d.fonti.giorni} giorni</span>}
    >
      <NumeroGrande valore={d.fonti.clienti} anima={anima} colore="text-brand-ink" dopo={<span className="text-brand-ink">{n(d.fonti.clienti) === 1 ? "contatto nuovo" : "contatti nuovi"}</span>} />
      <p className="text-small text-ink-2">
        {prima ? `Prima fonte: ${prima.fonte}, ${conteggio(n(prima.clienti), "contatto", "contatti")}.` : null}
        {senza > 0 ? ` ${conteggio(senza, "contatto", "contatti")} senza fonte.` : null}
      </p>
      <p className="text-small text-ink-2">
        {app === 0 ? "Nessun appuntamento nei prossimi 7 giorni." : `${conteggio(app, "appuntamento", "appuntamenti")} nei prossimi 7 giorni.`}
      </p>
    </Scheda>
  );
}

function Fermo({ d, assistenze, anima }) {
  const f = d.preventivi_fermi;
  return (
    <Scheda titolo="Cosa è fermo" testId="cruscotto-cosa-fermo" className="flex-1 gap-2.5">
      <NumeroGrande valore={f.numero} anima={anima} dopo={n(f.numero) === 1 ? "preventivo senza risposta" : "preventivi senza risposta"} />
      <p className="tabular-nums text-ink-2">
        {n(f.numero) === 0 ? (
          "Nessuno aspetta da più di 7 giorni."
        ) : (
          <>
            <strong className="text-ink">{euro(n(f.valore))}</strong> in attesa da più di 7 giorni
          </>
        )}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {n(f.scadono_7gg) > 0 ? <StatusBadge tone="warning">{conteggio(n(f.scadono_7gg), "link scade", "link scadono")} entro 7 giorni</StatusBadge> : null}
        {assistenze && n(assistenze.scadute) > 0 ? (
          <Link to="/assistenza" className="rounded-xs">
            <StatusBadge tone="danger">{conteggio(n(assistenze.scadute), "assistenza scaduta", "assistenze scadute")}</StatusBadge>
          </Link>
        ) : null}
        {assistenze && n(assistenze.scadute) === 0 && n(assistenze.aperte) > 0 ? (
          <Link to="/assistenza" className="rounded-xs">
            <StatusBadge tone="neutral">{conteggio(n(assistenze.aperte), "assistenza aperta", "assistenze aperte")}</StatusBadge>
          </Link>
        ) : null}
      </div>
    </Scheda>
  );
}

/* ── Da riprendere ─────────────────────────────────────────────────────────── */
function Giorni({ g }) {
  return (
    <span
      className={cn(
        "inline-flex w-12 shrink-0 justify-center rounded-xs py-0.5 text-caption font-bold tabular-nums",
        g >= 30 ? "bg-danger-bg text-danger-ink" : g >= 14 ? "bg-warning-bg text-warning-ink" : "bg-surface-3 text-ink-2",
      )}
    >
      {g} gg
    </span>
  );
}

function DaRiprendere({ f }) {
  const elenco = f.elenco || [];
  const altri = n(f.numero) - elenco.length;
  const adesso = Date.now();
  return (
    <Scheda
      id="fermi"
      titolo="Da riprendere, i più grandi"
      testId="cruscotto-fermi"
      className="flex-[3_1_560px] gap-1 pb-0"
      azione={
        <Link to="/preventivi/preventivatore" className="shrink-0 text-small font-semibold text-brand-ink underline-offset-[3px] hover:underline">
          Preventivatore
        </Link>
      }
    >
      <p className="-mt-1 text-small text-ink-2">Inviati da più di 7 giorni, senza risposta. Valore: la proposta più bassa.</p>
      {elenco.length === 0 ? (
        <p className="my-3 rounded-md bg-page px-4 py-4 text-ink-2">Nessun preventivo fermo da più di 7 giorni.</p>
      ) : (
        <div className="-mx-5 mt-2 border-t border-line-subtle md:-mx-6">
          {elenco.map((p) => {
            const scaduto = p.scadenza && new Date(p.scadenza).getTime() < adesso;
            return (
              <ListRow
                key={p.id}
                href={`/preventivi/preventivatore/${p.id}`}
                data-testid={`cruscotto-fermo-${p.id}`}
                className="last:border-b-0"
                leading={<Giorni g={n(p.giorni)} />}
                title={p.cliente || "Cliente senza nome"}
                meta={[p.titolo, p.venditore, p.scadenza ? (scaduto ? "link scaduto" : "link attivo") : "nessun link attivo"].filter(Boolean).join(" · ")}
                trailing={p.valore === null ? "—" : euro(n(p.valore))}
              />
            );
          })}
        </div>
      )}
      {altri > 0 || n(f.scadono_7gg) > 0 ? (
        <div className="-mx-5 flex flex-col gap-1 border-t border-line-subtle px-5 py-3 text-small text-ink-2 md:-mx-6 md:px-6">
          {altri > 0 ? <p>E altri {altri}: sono nel Preventivatore.</p> : null}
          {n(f.scadono_7gg) > 0 ? (
            <p data-testid="cruscotto-scadono">
              {f.scadono_7gg === 1
                ? "1 preventivo in attesa di risposta scade nei prossimi 7 giorni."
                : `${f.scadono_7gg} preventivi in attesa di risposta scadono nei prossimi 7 giorni.`}
            </p>
          ) : null}
        </div>
      ) : (
        <div className="h-2" />
      )}
    </Scheda>
  );
}

/* ── Da dove arrivano ──────────────────────────────────────────────────────── */
function Fonti({ f, anima }) {
  const elenco = f.elenco || [];
  const massimo = Math.max(1, ...elenco.map((x) => n(x.clienti)));
  return (
    <Scheda id="provenienza" titolo="Da dove arrivano" testId="cruscotto-fonti" azione={<span className="text-small text-ink-3">{f.giorni} giorni</span>}>
      {elenco.length === 0 ? (
        <p className="rounded-md bg-page px-4 py-4 text-ink-2">Nessun cliente nuovo negli ultimi {f.giorni} giorni.</p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {elenco.map((x, i) => {
            const nonIndicata = x.codice === "non_indicata";
            return (
              <li key={x.codice} className="flex flex-col gap-1">
                <div className="flex justify-between gap-2">
                  <span className={nonIndicata ? "text-ink-2" : "text-ink"}>{x.fonte}</span>
                  <span className="font-bold tabular-nums text-ink-strong">{x.clienti}</span>
                </div>
                <span className="block h-1.5 rounded-[3px] bg-line-subtle" aria-hidden="true">
                  <span
                    className={cn("block h-full rounded-[3px]", nonIndicata ? "bg-chart-neutral" : "bg-chart-1", anima && "ida-grow-x")}
                    style={{ width: `${Math.max(4, (n(x.clienti) / massimo) * 100)}%`, animationDelay: anima ? `${Math.min(i, 3) * 40}ms` : undefined }}
                  />
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </Scheda>
  );
}

/* ── Prossimi 7 giorni ─────────────────────────────────────────────────────── */
function Agenda({ a }) {
  const elenco = a.elenco || [];
  return (
    <Scheda
      titolo="Prossimi 7 giorni"
      testId="cruscotto-appuntamenti"
      azione={
        <Link to="/calendario" className="text-small font-semibold text-brand-ink underline-offset-[3px] hover:underline">
          Calendario
        </Link>
      }
    >
      {elenco.length === 0 ? (
        <p className="rounded-md bg-page px-4 py-4 text-ink-2">Nessun appuntamento nei prossimi 7 giorni.</p>
      ) : (
        <ul className="flex flex-col gap-3">
          {elenco.map((x) => (
            <li key={x.id} className="flex items-start gap-3">
              <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand-ink" aria-hidden="true">
                <CalendarDays className="size-5" strokeWidth={1.75} />
              </span>
              <span className="min-w-0">
                <span className="block font-semibold text-ink-strong">{giornoOra(x.data_inizio)}</span>
                <span className="block truncate text-small text-ink-2">
                  {[TIPO_APPUNTAMENTO[x.tipo] || "Appuntamento", x.cliente, x.sede].filter(Boolean).join(" · ")}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
      {n(a.numero) > elenco.length ? <p className="text-small text-ink-2">E altri {n(a.numero) - elenco.length} in calendario.</p> : null}
    </Scheda>
  );
}

function Caricamento() {
  return (
    <div className="flex flex-col gap-5" data-testid="cruscotto-caricamento" aria-busy="true">
      <div className="flex flex-wrap gap-5">
        <Skeleton className="h-[420px] min-w-0 flex-[2_1_600px] rounded-xl" />
        <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-5">
          <Skeleton className="h-[200px] rounded-xl" />
          <Skeleton className="h-[200px] rounded-xl" />
        </div>
      </div>
      <Skeleton className="h-64 w-full rounded-xl" />
      <span className="sr-only">Carico i numeri…</span>
    </div>
  );
}

export default function CruscottoPage() {
  const [periodo, setPeriodo] = useState("mese");
  const [sede, setSede] = useState(leggiSede);
  const [sedi, setSedi] = useState([]);
  const [dati, setDati] = useState(null);
  const [perSede, setPerSede] = useState(null);
  const [errore, setErrore] = useState(null);
  const [carica, setCarica] = useState(true);
  const [assistenze, setAssistenze] = useState(null);
  /* Cambiando sede o periodo in fretta, la risposta di prima può arrivare dopo e mettere i numeri di
     tutte le sedi sotto il selettore «Vasto». Vale solo l'ultima richiesta partita. */
  const ultima = useRef(0);
  /* Barre e numeri crescono solo alla prima apertura: con un filtro i dati cambiano senza movimento. */
  const [anima, setAnima] = useState(true);
  useEffect(() => {
    if (!dati) return undefined;
    const t = setTimeout(() => setAnima(false), 800);
    return () => clearTimeout(t);
  }, [dati]);

  // Sul computer la sede si sceglie nel menu blu (ShowroomSelector): la si segue.
  useEffect(() => {
    const segui = (e) => setSede(e.detail || "all");
    window.addEventListener("ida:sede", segui);
    return () => window.removeEventListener("ida:sede", segui);
  }, []);

  useEffect(() => {
    let vivo = true;
    api
      .get("/sedi")
      .then((r) => vivo && setSedi(r.data || []))
      .catch(() => vivo && setSedi([]));
    return () => {
      vivo = false;
    };
  }, []);

  const leggi = useCallback(async () => {
    const mia = ++ultima.current;
    setCarica(true);
    setErrore(null);
    try {
      const r = await api.get("/cruscotto", { params: { periodo, sede_id: sede === "all" ? undefined : sede } });
      if (mia === ultima.current) setDati(r.data);
    } catch (e) {
      if (mia === ultima.current) setErrore(messaggioDi(e));
    } finally {
      if (mia === ultima.current) setCarica(false);
    }
  }, [periodo, sede]);

  useEffect(() => {
    leggi();
  }, [leggi]);

  /* Con «Tutte le sedi» il venduto si divide per sede: la stessa API, una sede per volta. */
  useEffect(() => {
    setPerSede(null);
    if (sede !== "all" || sedi.length < 2) return undefined;
    let vivo = true;
    Promise.all(
      sedi.map((s) =>
        api.get("/cruscotto", { params: { periodo, sede_id: s.id } }).then((r) => ({ id: s.id, nome: s.nome, importo: n(r.data?.venduto?.importo) })),
      ),
    )
      .then((valori) => vivo && setPerSede({ periodo, valori }))
      .catch(() => vivo && setPerSede(null));
    return () => {
      vivo = false;
    };
  }, [periodo, sede, sedi]);

  // L'assistenza ha il suo conteggio (0039): aperte e scadute, sulla stessa sede.
  useEffect(() => {
    let vivo = true;
    api
      .get("/assistenze/conteggi", { params: { sede_id: sede === "all" ? undefined : sede } })
      .then((r) => vivo && setAssistenze(r.data))
      .catch(() => vivo && setAssistenze(null));
    return () => {
      vivo = false;
    };
  }, [sede]);

  const cambiaSede = (v) => {
    setSede(v);
    try {
      localStorage.setItem("ida:sede", v);
    } catch (_) {}
    window.dispatchEvent(new CustomEvent("ida:sede", { detail: v }));
  };

  const d = dati;
  const aggiornata = d?.aggiornato_at ? ` · aggiornata alle ${fmtOra.format(new Date(d.aggiornato_at))}` : "";
  return (
    <section data-testid="page-cruscotto" className="flex flex-col gap-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-h1-mobile text-ink-strong md:text-h1">Dashboard</h1>
          <p className="mt-1.5 text-ink-3">
            {maiuscola(fmtOggi.format(new Date()))}
            {aggiornata}
          </p>
        </div>
        <div className="grid w-full grid-cols-2 gap-2.5 sm:flex sm:w-auto" role="group" aria-label="Filtri della dashboard">
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="h-11 font-semibold sm:w-[190px]" data-testid="cruscotto-periodo" aria-label="Periodo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIODI.map((p) => (
                <SelectItem key={p.valore} value={p.valore}>
                  {p.etichetta}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* Senza il menu blu (sotto i 1024 px) la sede si sceglie qui. */}
          <Select value={sede} onValueChange={cambiaSede}>
            <SelectTrigger className="h-11 font-semibold sm:w-[190px] lg:hidden" data-testid="cruscotto-sede" aria-label="Sede">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le sedi</SelectItem>
              {sedi.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      {errore ? (
        <div className="rounded-xl border border-line bg-surface" data-testid="cruscotto-errore">
          <ErrorState
            title="I numeri non sono arrivati"
            actions={
              <Button variant="secondary" onClick={leggi}>
                <RefreshCw /> Riprova
              </Button>
            }
          >
            {errore}
          </ErrorState>
        </div>
      ) : null}

      {!d && carica && !errore ? <Caricamento /> : null}

      {d ? (
        <div className={cn("flex flex-col gap-5 transition-opacity duration-150", carica && "opacity-60")} aria-busy={carica}>
          <div className="flex flex-wrap gap-5">
            <Venduto d={d} perSede={perSede} anima={anima} />
            <div className="flex min-w-0 flex-[1_1_300px] flex-col gap-5">
              <ChiArriva d={d} anima={anima} />
              <Fermo d={d} assistenze={assistenze} anima={anima} />
            </div>
          </div>
          <div className="flex flex-wrap items-start gap-5">
            <DaRiprendere f={d.preventivi_fermi} />
            <div className="flex min-w-0 flex-[2_1_340px] flex-col gap-5">
              <Fonti f={d.fonti} anima={anima} />
              <Agenda a={d.appuntamenti} />
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
